import { useCallback, useEffect, useId, useMemo, useState, type SVGProps } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { MarketingEvent } from '@bcc/shared';
import { AnimatePresence, motion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import { useFloatingCorner } from '../contexts/FloatingCornerContext';
import { COMPANY_EMAIL, COMPANY_EMAIL_HREF } from '../constants/companyContact';
import { eventCornerDismissStorageKey } from '../constants/eventCornerDismiss';
import { apiUrl } from '../lib/apiUrl';
import { supabase } from '../lib/supabase';

const AUTO_SHOW_MS = 1000;

/** Tight vertical rhythm between FABs and panels (no overlap). */
const STACK_GAP = 'gap-2';

function IconEventFab(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={26} height={26} fill="none" stroke="currentColor" strokeWidth={1.9} aria-hidden {...props}>
      <rect x="3.5" y="5.25" width="17" height="15" rx="2.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 3.75v5M16 3.75v5M4 11.25h16" strokeLinecap="round" />
      <circle cx="9" cy="16" r="1.15" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="16" r="1.15" fill="currentColor" stroke="none" opacity={0.45} />
      <circle cx="16" cy="16" r="1.15" fill="currentColor" stroke="none" opacity={0.35} />
    </svg>
  );
}

function IconPin(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <path d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="11" r="2" />
    </svg>
  );
}

function IconClock(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <circle cx="12" cy="12" r="8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconMessenger(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={26} height={26} fill="currentColor" aria-hidden {...props}>
      <path d="M4 4h16a3 3 0 013 3v8a3 3 0 01-3 3h-4.5l-3.3 2.75a1 1 0 01-1.6-.8V18H4a3 3 0 01-3-3V7a3 3 0 013-3z" />
      <circle cx="8.75" cy="12" r="1.15" opacity={0.85} />
      <circle cx="12" cy="12" r="1.15" opacity={0.85} />
      <circle cx="15.25" cy="12" r="1.15" opacity={0.85} />
    </svg>
  );
}

const eventFabClass =
  'pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-[var(--header-shell-bg)] shadow-[0_12px_40px_-10px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,1)] ring-2 ring-black/[0.08] transition hover:brightness-[1.02] hover:ring-[var(--header-shell-bg)]/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--header-shell-bg)]';

const supportFabClass =
  'pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--header-shell-bg)] text-white shadow-[0_10px_40px_-8px_rgba(0,0,0,0.45),0_2px_0_rgba(255,255,255,0.25)_inset] ring-2 ring-white/40 transition hover:brightness-110 hover:ring-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

type Props = {
  reduceMotion?: boolean;
  /** Event card eyebrow (copy only; data comes from API) */
  eyebrowLead?: string;
  eyebrowHighlight?: string;
};

type CornerEventCard = {
  day: string;
  month: string;
  title: string;
  locationLabel: string;
  timeLabel: string;
  thumbSrc?: string;
  thumbAlt: string;
};

function cornerCardFromMarketingEvent(ev: MarketingEvent): CornerEventCard {
  const tz = ev.timezone || 'UTC';
  const start = new Date(ev.starts_at);
  const partsStart = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', timeZone: tz }).formatToParts(start);
  const dayStart = partsStart.find((p) => p.type === 'day')?.value ?? '01';
  const monthLabel = (partsStart.find((p) => p.type === 'month')?.value ?? 'Jan').toUpperCase();

  let day = dayStart;
  if (ev.ends_at) {
    const end = new Date(ev.ends_at);
    const partsEnd = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', timeZone: tz }).formatToParts(end);
    const dayEnd = partsEnd.find((p) => p.type === 'day')?.value ?? dayStart;
    if (dayEnd !== dayStart) day = `${dayStart}–${dayEnd}`;
  }

  let timeLabel = ev.time_label?.trim() ?? '';
  if (!timeLabel) {
    const tf = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
    if (ev.ends_at) {
      timeLabel = `${tf.format(start)} – ${tf.format(new Date(ev.ends_at))}`;
    } else {
      timeLabel = tf.format(start);
    }
  }

  let locationLabel: string;
  if (ev.is_online) {
    const v = ev.venue?.trim();
    locationLabel = v || 'Virtual event';
  } else {
    locationLabel = ev.venue?.trim() || 'TBA';
  }

  const thumbSrc = ev.image_path
    ? supabase.storage.from('event-assets').getPublicUrl(ev.image_path).data.publicUrl
    : undefined;

  return {
    day,
    month: monthLabel,
    title: ev.title,
    locationLabel,
    timeLabel,
    thumbSrc,
    thumbAlt: ev.title ? `${ev.title} thumbnail` : 'Event thumbnail',
  };
}

/**
 * Single bottom-right column: support FAB (lowest), help panel, event FAB, event panel.
 * Avoids overlapping when both dialogs are open.
 */
export function MarketingCornerPopoverStack({
  reduceMotion,
  eyebrowLead = 'NEXT LIVE',
  eyebrowHighlight = 'EVENT',
}: Props) {
  const eventTitleId = useId();
  const supportTitleId = useId();
  const { session } = useAuth();
  const signedInUserId = session?.user?.id ?? null;
  const { setEventAnnouncementExpanded, setSupportMessengerOpen } = useFloatingCorner();

  const [eventExpanded, setEventExpanded] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [nextEvent, setNextEvent] = useState<MarketingEvent | null | undefined>(undefined);

  const card = useMemo(() => (nextEvent ? cornerCardFromMarketingEvent(nextEvent) : null), [nextEvent]);

  const dismissStorageKey =
    signedInUserId && nextEvent?.id ? eventCornerDismissStorageKey(signedInUserId, nextEvent.id) : null;

  const rememberEventAnnouncementDismissed = useCallback(() => {
    if (!dismissStorageKey) return;
    try {
      localStorage.setItem(dismissStorageKey, '1');
    } catch {
      /* quota / private mode */
    }
  }, [dismissStorageKey]);

  const collapseEventAnnouncement = useCallback(() => {
    rememberEventAnnouncementDismissed();
    setEventExpanded(false);
  }, [rememberEventAnnouncementDismissed]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(apiUrl('/api/public/events'));
        if (!res.ok) {
          if (!cancelled) setNextEvent(null);
          return;
        }
        const data = (await res.json()) as { upcoming?: MarketingEvent[] };
        const first = data.upcoming?.[0];
        if (!cancelled) setNextEvent(first ?? null);
      } catch {
        if (!cancelled) setNextEvent(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const on = Boolean(card && eventExpanded);
    setEventAnnouncementExpanded(on);
    return () => setEventAnnouncementExpanded(false);
  }, [card, eventExpanded, setEventAnnouncementExpanded]);

  useEffect(() => {
    setSupportMessengerOpen(supportOpen);
    return () => setSupportMessengerOpen(false);
  }, [supportOpen, setSupportMessengerOpen]);

  useEffect(() => {
    if (!card || !nextEvent) return;
    if (dismissStorageKey) {
      try {
        if (localStorage.getItem(dismissStorageKey) === '1') return;
      } catch {
        /* ignore */
      }
    }
    const t = window.setTimeout(() => setEventExpanded(true), AUTO_SHOW_MS);
    return () => window.clearTimeout(t);
  }, [card, nextEvent, dismissStorageKey]);

  useEffect(() => {
    if (!eventExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') collapseEventAnnouncement();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [eventExpanded, collapseEventAnnouncement]);

  useEffect(() => {
    if (!supportOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSupportOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [supportOpen]);

  const eventCardMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 16, scale: 0.97 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 12, scale: 0.97 },
        transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
      };

  const supportCardMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 14, scale: 0.96 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 10, scale: 0.96 },
        transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] as const },
      };

  const safeBottom = `max(1.25rem, env(safe-area-inset-bottom, 0px))`;
  const safeRight = `max(1rem, env(safe-area-inset-right, 0px))`;

  const shell = (
    <div
      className={`fixed z-[303] flex flex-col items-end justify-end ${STACK_GAP} pointer-events-none`}
      /** justify-end + column: last child (support FAB) is flush to safe bottom; stack grows upward. */
      style={{ bottom: safeBottom, right: safeRight }}
    >
      {card ? (
        <>
          <AnimatePresence>
            {eventExpanded ? (
              <motion.div key="bcc-event-corner" className="flex flex-col items-end gap-2" {...eventCardMotion}>
                <p className="pointer-events-none pr-1 text-right font-display text-[0.625rem] font-semibold uppercase tracking-[0.2em] text-white/95 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
                  {eyebrowLead}{' '}
                  <span className="text-[var(--header-shell-bg)] drop-shadow-[0_1px_2px_rgb(10_18_36/0.5)]">{eyebrowHighlight}</span>
                </p>
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={eventTitleId}
                  className="pointer-events-auto relative w-[min(calc(100vw-2rem),19.75rem)] rounded-2xl border border-neutral-200/90 bg-white p-4 pr-11 shadow-[0_28px_60px_-18px_rgba(0,0,0,0.45)]"
                >
                  <button
                    type="button"
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-700"
                    onClick={() => collapseEventAnnouncement()}
                    aria-label="Close"
                  >
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden className="-translate-x-px translate-y-px">
                      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                    </svg>
                  </button>
                  <div className="flex items-start gap-3">
                    {card.thumbSrc ? (
                      <img
                        src={card.thumbSrc}
                        alt={card.thumbAlt}
                        className="h-[4.5rem] w-[6rem] shrink-0 rounded-[10px] object-cover shadow-sm ring-1 ring-black/[0.06]"
                      />
                    ) : (
                      <div className="flex h-[4.5rem] w-[6rem] shrink-0 flex-col justify-end rounded-[10px] bg-gradient-to-br from-[#1e3a5f] via-[#0f2744] to-[#061525] p-2 shadow-sm ring-1 ring-black/[0.08]">
                        <span className="font-display text-[0.5rem] font-bold uppercase leading-tight tracking-wide text-[var(--home-miami-blue)]">
                          Live briefing
                        </span>
                      </div>
                    )}
                    <div className="flex min-w-[2.65rem] shrink-0 flex-col items-start leading-none">
                      <span className="font-body text-[2.375rem] font-bold tabular-nums tracking-tight text-[var(--header-shell-bg)]">
                        {card.day}
                      </span>
                      <span className="mt-1.5 font-body text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-700">
                        {card.month}
                      </span>
                    </div>
                  </div>
                  <p
                    id={eventTitleId}
                    className="mt-3 border-b border-[var(--home-miami-blue)]/35 pb-1.5 font-body text-[1rem] font-bold uppercase tracking-[0.07em] text-neutral-900"
                  >
                    {card.title}
                  </p>
                  <ul className="mt-3 space-y-2 font-body text-[0.8125rem] text-neutral-500">
                    <li className="flex items-center gap-2">
                      <span className="shrink-0 text-neutral-400" aria-hidden>
                        <IconPin />
                      </span>
                      <span>{card.locationLabel}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="shrink-0 text-neutral-400" aria-hidden>
                        <IconClock />
                      </span>
                      <span>{card.timeLabel}</span>
                    </li>
                  </ul>
                  <motion.div
                    className="mt-4 w-full origin-center"
                    {...(reduceMotion
                      ? {}
                      : {
                          animate: { scale: [1, 1.058, 1] },
                          transition: {
                            duration: 2.25,
                            repeat: Infinity,
                            ease: [0.45, 0.05, 0.55, 0.95] as const,
                          },
                        })}
                  >
                    <Link
                      to="/events#events-schedule"
                      className="flex w-full items-center justify-center rounded-full bg-[var(--header-shell-bg)] px-4 py-2.5 text-center font-body text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-white shadow-md shadow-black/15 transition hover:brightness-110"
                      onClick={() => collapseEventAnnouncement()}
                    >
                      RSVP / reminders
                    </Link>
                  </motion.div>
                  <button
                    type="button"
                    className="mt-2 w-full text-center font-body text-[0.7rem] font-medium uppercase tracking-[0.08em] text-neutral-400 underline-offset-2 hover:text-neutral-600 hover:underline"
                    onClick={() => collapseEventAnnouncement()}
                  >
                    Minimize
                  </button>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <button
            type="button"
            className={`${eventFabClass} relative shrink-0`}
            onClick={() =>
              setEventExpanded((e) => {
                if (e) {
                  rememberEventAnnouncementDismissed();
                  return false;
                }
                return true;
              })
            }
            aria-label={eventExpanded ? 'Minimize event announcement' : 'Expand upcoming event'}
            aria-expanded={eventExpanded}
            aria-haspopup="dialog"
          >
            <IconEventFab />
            {!eventExpanded ? (
              <span
                className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-[11px] min-w-[11px] items-center justify-center rounded-full bg-[var(--header-shell-bg)] ring-2 ring-white"
                aria-hidden
              />
            ) : null}
          </button>
        </>
      ) : null}

      <AnimatePresence>
        {supportOpen ? (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={supportTitleId}
            className="pointer-events-auto w-[min(calc(100vw-2rem),20rem)] rounded-2xl border border-white/15 bg-[var(--home-zone-accent)]/97 px-4 py-4 text-[var(--home-text-on-dark)] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.55)] ring-1 ring-black/35 backdrop-blur-md"
            {...supportCardMotion}
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--header-shell-bg)]/35 text-white">
                <IconMessenger width={22} height={22} />
              </span>
              <div className="min-w-0 flex-1 pt-0.5">
                <p id={supportTitleId} className="font-display text-[0.95rem] font-bold leading-snug tracking-tight">
                  How can we help?
                </p>
                <p className="mt-2 font-body text-[0.8125rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                  Reach our team about offerings, underwriting, or your investor dashboard.
                </p>
                <p className="mt-3 font-body text-[0.8125rem] text-[var(--home-muted-on-dark)]">
                  <a
                    href={COMPANY_EMAIL_HREF}
                    className="font-medium text-[var(--home-text-on-dark)] underline underline-offset-2 hover:text-[var(--home-miami-blue)]"
                  >
                    {COMPANY_EMAIL}
                  </a>
                </p>
              </div>
              <button
                type="button"
                className="-m-1 shrink-0 rounded-lg p-1 text-[var(--home-muted-on-dark)] transition hover:bg-white/10 hover:text-[var(--home-text-on-dark)]"
                onClick={() => setSupportOpen(false)}
                aria-label="Close"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/contact"
                className="inline-flex min-h-[2.5rem] min-w-[5.5rem] flex-1 items-center justify-center rounded-xl bg-[var(--home-miami-blue)] px-3 py-2 text-center text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105"
                onClick={() => setSupportOpen(false)}
              >
                Contact
              </Link>
              <button
                type="button"
                className="inline-flex min-h-[2.5rem] min-w-[5.5rem] flex-1 items-center justify-center rounded-xl border border-white/20 bg-white/[0.06] px-3 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] transition hover:bg-white/12"
                onClick={() => setSupportOpen(false)}
              >
                Close
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <button
        type="button"
        className={`${supportFabClass} relative shrink-0`}
        onClick={() => setSupportOpen((o) => !o)}
        aria-label={supportOpen ? 'Close support messenger' : 'Open support messenger'}
        aria-expanded={supportOpen}
        aria-haspopup="dialog"
      >
        <IconMessenger />
      </button>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(shell, document.body) : null;
}
